import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { PHONE_MAX_WIDTH } from "../src/pane-layout.ts";
import { CheckIcon } from "./icons.tsx";

/** The width above which the phone layout, and so every tray, gives way to the desktop one. */
const PHONE_QUERY = `(max-width: ${PHONE_MAX_WIDTH}px)`;

const FOCUSABLE = "a[href], button:not([disabled]), input, [tabindex]:not([tabindex='-1'])";

/*
 * A phone bottom tray: a dialog that rises from the bottom edge over a dimmed page. It holds focus
 * while open, closes on Escape, on the backdrop or on its own Done button, returns focus to what
 * opened it, locks the page scroll behind it and closes itself if the window grows past a phone.
 * The page behind is inert meanwhile, so neither Tab nor a screen reader's cursor reaches it.
 * Motion is a short slide; `prefers-reduced-motion` turns it into a plain appearance in the CSS.
 */
export function Tray(props: {
  open: boolean;
  title: string;
  /**
   * Drawn in the head in the title's place, which then names the dialog for assistive technology
   * only: a composer's mode menu, so the tray has one header, not a title over a second one.
   */
  heading?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { open, onClose } = props;
  const panel = useRef<HTMLDivElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const titleId = useId();
  /* The caller passes a fresh closure each render; the effect must not restart on every one. */
  const closer = useRef(onClose);
  closer.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.focus();
    const behind = [...(layer.current?.parentElement?.children ?? [])].filter(
      (element) => element !== layer.current && !element.hasAttribute("inert"),
    );
    for (const element of behind) element.setAttribute("inert", "");
    const query = window.matchMedia(PHONE_QUERY);
    const leave = () => {
      if (!query.matches) closer.current();
    };
    query.addEventListener("change", leave);
    return () => {
      query.removeEventListener("change", leave);
      document.body.style.overflow = overflow;
      // Given back before the focus is, or the opener could not take it.
      for (const element of behind) element.removeAttribute("inert");
      opener?.focus();
    };
  }, [open]);

  if (!open) return null;

  const keys = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const items = [...(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) return;
    if (
      event.shiftKey &&
      (document.activeElement === first || document.activeElement === panel.current)
    ) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="lcr-tray-layer" ref={layer}>
      <div className="lcr-tray-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        className="lcr-tray"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={keys}
      >
        <span className="lcr-tray-grip" aria-hidden="true" />
        <header className="lcr-tray-head">
          <h2 id={titleId} className={props.heading ? "lcr-visually-hidden" : undefined}>
            {props.title}
          </h2>
          {props.heading}
          {/* "Close", never "Done": Done is the inbox's archive action (design review #12). */}
          <button type="button" className="lcr-tray-done" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="lcr-tray-body">{props.children}</div>
        {props.footer && <footer className="lcr-tray-foot">{props.footer}</footer>}
      </div>
    </div>,
    document.getElementById("leadcue-reply-rep-preview") ?? document.body,
  );
}

/** `detail` is a second line under the label, as a menu option carries one; `count` a total. */
export type TrayChoice = {
  value: string;
  label: string;
  detail?: string;
  count?: { value: number; noun: string };
};

/** One choice among several, as full-width rows: the whole row is the target and one is current. */
export function TrayChoices(props: {
  label: string;
  value: string;
  choices: readonly TrayChoice[];
  onChange: (value: string) => void;
  icon?: ReactNode;
}) {
  const id = useId();
  const counted = props.choices.some((choice) => choice.count);
  return (
    <section className="lcr-tray-section" aria-labelledby={id}>
      <h3 id={id} className="lcr-tray-label">
        {props.label}
      </h3>
      <fieldset aria-labelledby={id} className="lcr-tray-list">
        {props.choices.map((choice) => (
          <button
            type="button"
            key={choice.value}
            aria-pressed={choice.value === props.value}
            className="lcr-tray-row"
            onClick={() => props.onChange(choice.value)}
          >
            {props.icon}
            <span className="lcr-tray-row-text" title={choice.label}>
              <span className="lcr-tray-row-label">{choice.label}</span>
              {choice.detail && <span className="lcr-tray-row-detail">{choice.detail}</span>}
            </span>
            {choice.count && (
              <span className="lcr-tray-row-count">
                <span aria-hidden="true">{choice.count.value.toLocaleString("en-GB")}</span>
                <span className="lcr-visually-hidden">
                  , {choice.count.value.toLocaleString("en-GB")} {choice.count.noun}
                </span>
              </span>
            )}
            {choice.value === props.value ? (
              <CheckIcon size={18} />
            ) : (
              counted && <span className="lcr-tray-tick" aria-hidden="true" />
            )}
          </button>
        ))}
      </fieldset>
    </section>
  );
}
